/* eslint-disable no-console */
/**
 * @fileoverview UI Layer hook that builds memoized socket configuration for the Inbox.
 * @module useInboxSocketConfig
 * @category Hook - UI Layer
 * @since 3.2.0
 * @version 3.3.0 - Documented support for multiple sockets and rooms.
 *
 * @see {@link ../../actions/useInboxSocketActions} Hook of Actions Layer for handlers
 * @see {@link ../../data/useInboxSocketEvents} Hook of Data Layer for event subscription
 */

import { useMemo } from "react";
import { isInboxSocketDebugEnabled } from "../../../utils/inboxSocketDebug";

/**
 * Global list of events supported by the Inbox socket system.
 * Defined at module scope to keep a stable reference across renders.
 * @type {Array<string>}
 */
const SOCKET_SUPPORTED_EVENTS = [
  'customer_update',
  'new_message',
  'message_update',
  'chat_counter_update'
];

/**
 * @typedef {Object} SocketEventHandlers
 * @description Event handlers injected from the Actions Layer.
 * @property {(event: Object) => void} onNewMessage - Handler for new messages
 * @property {(event: Object) => void} onCustomerUpdate - Handler for customer updates
 * @property {(event: Object) => void} onMessageUpdate - Handler for message updates
 */

/**
 * @typedef {Object} SocketConnectionConfig
 * @description Connection configuration for Inbox sockets.
 * @property {Object} [socketInstance] - Single socket instance
 * @property {() => Object} [getSocket] - Factory for resolving a single instance
 * @property {string} [socketUrl] - Single socket URL
 * @property {Object} [socketOptions] - Connection options for `socketUrl`
 * @property {Array<Object>} [socketInstances] - List of socket instances
 * @property {() => Array<Object>} [getSockets] - Factory for resolving multiple instances
 * @property {Array<string>} [socketUrls] - List of socket URLs
 * @property {Array<Object>} [socketOptionsList] - Per-URL options (same index as `socketUrls`)
 * @property {(string|number)} [roomId] - Main room subscribed with `create_room`
 * @property {Array<(string|number)>} [additionalRoomIds] - Secondary room IDs (`other_workspace_<id>`)
 */

/**
 * @typedef {Object} SocketConfig
 * @description Final configuration consumed by `useInboxSocketEvents`.
 * @property {Array<string>} enabledEvents - Enabled socket events
 * @property {(event: Object) => void} onNewMessage - Callback for new messages
 * @property {(event: Object) => void} onCustomerUpdate - Callback for customer updates
 * @property {(event: Object) => void} onMessageUpdate - Callback for message updates
 * @property {Object} [socketInstance] - Single socket instance
 * @property {() => Object} [getSocket] - Factory for single instance
 * @property {string} [socketUrl] - Single socket URL
 * @property {Object} [socketOptions] - Connection options for `socketUrl`
 * @property {Array<Object>} [socketInstances] - List of socket instances
 * @property {() => Array<Object>} [getSockets] - Factory for multiple sockets
 * @property {Array<string>} [socketUrls] - List of socket URLs
 * @property {Array<Object>} [socketOptionsList] - Per-URL options
 * @property {(string|number)} [roomId] - Main room
 * @property {Array<(string|number)>} [additionalRoomIds] - Secondary rooms
 */

/**
 * @typedef {Object} UseInboxSocketConfigReturn
 * @description Return object of `useInboxSocketConfig`.
 * @property {SocketConfig} socketConfig - Complete config for `useInboxSocketEvents`
 */

/**
 * UI Layer hook for static and memoized Inbox WebSocket configuration.
 *
 * Implements the **UI Layer** pattern with strict separation of concerns,
 * providing only configuration and handler composition, without business logic or effects.
 *
 * **Responsibilities:**
 * - Define enabled events for the Inbox
 * - Compose final config for `useInboxSocketEvents`
 * - Inject Actions Layer handlers without executing business logic
 * - Provide memoized static configuration
 *
 * **Does Not Handle:**
 * - ❌ Business logic or Redux actions (belongs to Actions Layer)
 * - ❌ Event processing or transformations
 * - ❌ Mutable state or side effects
 * - ❌ API calls or async operations
 *
 * **Performance:**
 * - useMemo: Memoized configuration to avoid reconnections
 * - Static Events: Predefined event list without recalculation
 * - Handler Injection: Stable handlers from Actions Layer
 * - Minimal Dependencies: Depends only on external handlers
 *
 * @function useInboxSocketConfig
 * @category Hook - UI Layer
 * @module useInboxSocketConfig
 * @since 3.1.0
 * @version 3.3.0 - Documentation aligned with multi-socket configuration.
 *
 * @param {SocketEventHandlers} eventHandlers - Event handlers from Actions Layer
 * @param {SocketConnectionConfig|null} [socketConnectionConfig=null] - Optional connection override; if omitted, defaults are resolved from `ENV`.
 *
 * @returns {UseInboxSocketConfigReturn} Complete WebSocket configuration
 *
 * @example
 * // Typical composition in a UI Layer component:
 * import useInboxSocketActions from '../actions/useInboxSocketActions';
 * import useInboxSocketConfig from './useInboxSocketConfig';
 *
 * const { socketEventHandlers } = useInboxSocketActions();
 * const { socketConfig } = useInboxSocketConfig(socketEventHandlers);
 *
 * // Pass socketConfig to useInboxSocketEvents (Data Layer)
 * const socketEvents = useInboxSocketEvents(socketConfig);
 *
 * @example
 * // Explicit override with two sockets:
 * const { socketConfig } = useInboxSocketConfig(socketEventHandlers, {
 *   socketUrls: [
 *     ENV.WEBSOCKET_URL,
 *     ENV.NOTIFICATIONS_MICROSERVICE_WEBSOCKET_URL
 *   ],
 *   socketOptionsList: [{ transports: ['websocket'] }, { transports: ['websocket'] }],
 *   roomId: window.roomId,
 *   additionalRoomIds: window.allWorkspaceIds
 * });
 *
 * @see {@link ../../actions/useInboxSocketActions}
 * @see {@link ../../data/useInboxSocketEvents}
 */
function useInboxSocketConfig(eventHandlers, socketConnectionConfig = null) {
  const resolvedSocketConnectionConfig = useMemo(() => {
    if (socketConnectionConfig && Object.keys(socketConnectionConfig).length > 0) {
      return socketConnectionConfig;
    }

    const primarySocketUrl = typeof ENV !== 'undefined' ? ENV.WEBSOCKET_URL : null;
    const secondarySocketUrl = typeof ENV !== 'undefined'
      ? ENV.NOTIFICATIONS_MICROSERVICE_WEBSOCKET_URL
      : null;

    const defaultSocketUrls = [primarySocketUrl, secondarySocketUrl]
      .filter(Boolean)
      .filter((url, index, urls) => urls.indexOf(url) === index);
    const defaultRoomId = typeof window !== 'undefined' ? window.roomId : null;
    const defaultAdditionalRoomIds = typeof window !== 'undefined' ? (window.allWorkspaceIds || []) : [];

    return {
      socketUrls: defaultSocketUrls,
      socketOptionsList: defaultSocketUrls.map(() => ({ transports: ['websocket'] })),
      roomId: defaultRoomId,
      additionalRoomIds: defaultAdditionalRoomIds
    };
  }, [socketConnectionConfig]);

  const {
    socketInstance,
    getSocket,
    socketUrl,
    socketOptions,
    socketInstances,
    getSockets,
    socketUrls,
    socketOptionsList,
    roomId,
    additionalRoomIds
  } = resolvedSocketConnectionConfig;
  /**
   * Memoized WebSocket config for Inbox
   *
   * @type {SocketConfig}
   * @memoized
   * @description Complete WebSocket config combining static events
   * with dynamic handlers from Actions Layer. Memoized to avoid
   * unnecessary socket reconnections.
   *
   * @dependencies [eventHandlers] - Recomputed only when handlers change
   * @performance Avoids config recreation on every render
   */
  /**
   * Allows `enabledEvents` to be passed as a prop to filter subscribed events,
   * or defaults to all globally supported events.
   */
  const socketConfig = useMemo(() => {
    if (isInboxSocketDebugEnabled()) {
      console.log('🔄 useInboxSocketConfig: Recreando socketConfig debido a cambios en eventHandlers');
    }

    return {
      enabledEvents: SOCKET_SUPPORTED_EVENTS,
      onNewMessage: eventHandlers?.onNewMessage,
      onCustomerUpdate: eventHandlers?.onCustomerUpdate,
      onMessageUpdate: eventHandlers?.onMessageUpdate,
      onChatCounterUpdate: eventHandlers?.onChatCounterUpdate,
      socketInstance,
      getSocket,
      socketUrl,
      socketOptions,
      socketInstances,
      getSockets,
      socketUrls,
      socketOptionsList,
      roomId,
      additionalRoomIds
    };
  }, [
    eventHandlers,
    socketInstance,
    getSocket,
    socketUrl,
    socketOptions,
    socketInstances,
    getSockets,
    socketUrls,
    socketOptionsList,
    roomId,
    additionalRoomIds
  ]);

  /**
   * Memoized return value of the hook
   *
   * @type {UseInboxSocketConfigReturn}
   * @memoized
   * @description Memoized WebSocket configuration with injected handlers.
   */
  const returnValue = useMemo(() => ({
    socketConfig
  }), [socketConfig]);

  return returnValue;
}

export default useInboxSocketConfig;
